import { useState, useEffect } from 'react';

interface Message {
  id: string;
  sender: 'user' | 'bot';
  text: string;
  timestamp: string;
  isConsentRequest?: boolean;
}

const STORAGE_KEY = 'chatWidgetHistory';
const CONSENT_KEY = 'chatWidgetConsent';
const WIDGET_VERSION = '2.0';

export const useChatStorage = (isAuthenticated: boolean) => {
  const WELCOME_MESSAGE: Message = {
    id: 'welcome',
    sender: 'bot',
    text: "Hello! I'm the Support Bot, your website navigation assistant. You can ask me where to find things on the page, or leave feedback. For scientific data questions, visit our AIDA at /Assistant",
    timestamp: new Date().toISOString(),
  };

  const [messages, setMessages] = useState<Message[]>(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      const savedVersion = sessionStorage.getItem('chatWidgetVersion');

      if (saved && savedVersion === WIDGET_VERSION) {
        const parsed = JSON.parse(saved);
        if (parsed.length > 0) {
          return parsed;
        }
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(CONSENT_KEY);
        sessionStorage.setItem('chatWidgetVersion', WIDGET_VERSION);
      }
    } catch (error) {
      console.error('Error loading chat history:', error);
    }
    return [WELCOME_MESSAGE];
  });

  // Save messages to storage
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
    } catch (error) {
      console.error('Error saving chat history:', error);
    }
  }, [messages]);

  // Clear on logout
  useEffect(() => {
    if (!isAuthenticated && messages.length > 1) {
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(CONSENT_KEY);
      setMessages([WELCOME_MESSAGE]);
    }
  }, [isAuthenticated]);

  const addMessage = (message: Message) => {
    setMessages(prev => [...prev, message]);
  };

  const resetMessages = () => {
    setMessages([WELCOME_MESSAGE]);
  };

  return {
    messages,
    setMessages,
    addMessage,
    resetMessages,
    WELCOME_MESSAGE,
  };
};
